/* eslint-disable eqeqeq */
import { useCallback, useEffect, useState } from "react";
import { API, get } from "../config";
import {
  formatNumber,
  formatSubcontractorName,
  getModuleNamingBySlug,
} from "./Misc";

export const useStateList = () => {
  const [stateList, setStateList] = useState([]);
  const [stateLoading, setStateLoading] = useState(false);
  const init = useCallback(async () => {
    try {
      setStateLoading(true);
      const { data } = await get(API.GET_STATE_LIST);
      setStateLoading(false);
      setStateList(data?.data);
    } catch (e) {
      setStateList([]);
      setStateLoading(false);
    }
  }, []);

  useEffect(() => {
    init();
  }, [init]);

  return {
    stateList,
    stateLoading,
  };
};

export const useContactTypeList = (trustId) => {
  const [contactTypesList, setContactTypesList] = useState([]);
  const [contactTypesLoading, setContactTypesLoading] = useState(false);
  const init = useCallback(async () => {
    try {
      setContactTypesLoading(true);
      const { data } = await get(
        `${API.GET_CONTACT_TYPE}?limit=-1&trust_id=${trustId}`,
      );
      setContactTypesLoading(false);
      setContactTypesList(data?.data?.data);
    } catch (e) {
      setContactTypesList([]);
      setContactTypesLoading(false);
    }
  }, [trustId]);

  useEffect(() => {
    init();
  }, [init]);

  return {
    contactTypesList,
    contactTypesLoading,
  };
};

export const SuburbList = async (
  state_id,
  page,
  setSuburbLoading,
  setSuburbList,
  appendData,
  search = "",
) => {
  try {
    if (state_id) {
      setSuburbLoading(true);

      const { data } = await get(
        `${API.GET_SUBURB_LIST}?state=${state_id}&limit=100&page=${page}&search=${search}`,
      );

      setSuburbLoading(false);

      setSuburbList((prevList) =>
        Array.isArray(prevList)
          ? appendData && data?.data?.data
            ? [...prevList, ...data.data.data]
            : data.data.data
          : data.data.data,
      );

      return data.data.last_page >= page ? true : false;
    } else {
      return false;
    } // Return whether there's more data
  } catch (e) {
    setSuburbList([]);
    setSuburbLoading(false);
    return false;
  }
};

export const ChartOfAccountList = async (
  trustId,
  type,
  page,
  setChartOfAccountLoading,
  setChartOfAccountList,
  appendData,
  search = "",
) => {
  try {
    setChartOfAccountLoading(true);

    const { data } = await get(
      `${API.GET_ACCOUNT_DATA}?trust_id=${trustId}&limit=50&page=${page}&search=${search}&type=${type}`,
    );

    setChartOfAccountLoading(false);

    setChartOfAccountList((prevList) =>
      Array.isArray(prevList)
        ? appendData && data?.data?.data
          ? [
              ...prevList,
              ...data.data.data,
              { name: "Add Chart Of Account", id: "new" },
            ]
          : [...data.data.data, { name: "Add Chart Of Account", id: "new" }]
        : [...data.data.data, { name: "Add Chart Of Account", id: "new" }],
    );

    return data.data.last_page >= page ? true : false;
  } catch (e) {
    setChartOfAccountList([
      { first_name: "", last_name: "Add ChartOfAccount", id: "new" },
    ]);
    setChartOfAccountLoading(false);
    return false;
  }
};

export const VendorList = async (
  organizationData,
  trustId,
  page,
  setVendorLoading,
  setVendorList,
  appendData,
  amlctfContact = false,
  organizationId,
  search = "",
) => {
  try {
    setVendorLoading(true);

    const { data } = await get(
      `${API.GET_VENDORS}?trust_id=${trustId}&limit=50&page=${page}&search=${search}&is_aml_ctf=${amlctfContact ? 1 : ""}${organizationId ? `&organization_id=${organizationId}` : ""}`,
    );

    setVendorLoading(false);

    setVendorList((prevList) =>
      Array.isArray(prevList)
        ? appendData && data?.data?.data
          ? [
              ...prevList,
              ...data.data.data,
              {
                first_name: "",
                last_name: `Add ${getModuleNamingBySlug(
                  organizationData?.organizationData,
                  "vendors",
                  "Vendor",
                )}`,
                id: "new",
              },
            ]
          : [
              ...data.data.data,
              {
                first_name: "",
                last_name: `Add ${getModuleNamingBySlug(
                  organizationData?.organizationData,
                  "vendors",
                  "Vendor",
                )}`,
                id: "new",
              },
            ]
        : [
            ...data.data.data,
            {
              first_name: "",
              last_name: `Add ${getModuleNamingBySlug(
                organizationData?.organizationData,
                "vendors",
                "Vendor",
              )}`,
              id: "new",
            },
          ],
    );

    return data.data.last_page >= page ? true : false;
  } catch (e) {
    setVendorList([
      {
        first_name: "",
        last_name: `Add ${getModuleNamingBySlug(
          organizationData?.organizationData,
          "vendors",
          "Vendor",
        )}`,
        id: "new",
      },
    ]);
    setVendorLoading(false);
    return false;
  }
};

export const orgVendorList = async (
  trustId,
  page,
  setVendorLoading,
  setVendorList,
  appendData,
  search = "",
) => {
  if (trustId?.length > 0) {
    try {
      setVendorLoading(true);

      const { data } = await get(
        `${API.GET_VENDORS}?trust_id=${
          Array.isArray(trustId) && trustId?.length > 0
            ? trustId?.join(",")
            : trustId
              ? trustId
              : ""
        }&limit=50&page=${page}&search=${search}`,
      );

      setVendorLoading(false);

      setVendorList((prevList) =>
        Array.isArray(prevList)
          ? appendData && data?.data?.data
            ? [...prevList, ...data.data.data]
            : [...data.data.data]
          : [...data.data.data],
      );

      return data.data.last_page >= page ? true : false;
    } catch (e) {
      setVendorList([]);
      setVendorLoading(false);
      return false;
    }
  }
};

export const AssetList = async (
  organizationData,
  trustId,
  vendorId,
  page,
  setAssetLoading,
  setAssetList,
  appendData,
  setListingHelper,
  targetIndex,
  search = "",
) => {
  try {
    if (vendorId) {
      setAssetLoading(true);

      const { data } = await get(
        `${API.GET_ASSET}?vendor_id=${vendorId}&limit=50&page=${page}&search=${search}&trust_id=${trustId}`,
      );

      setAssetLoading(false);

      setAssetList &&
        setAssetList((prevList) =>
          Array.isArray(prevList)
            ? appendData && data?.data?.data
              ? [
                  ...prevList,
                  ...data.data.data,
                  {
                    name: `Add ${getModuleNamingBySlug(
                      organizationData?.organizationData,
                      "assets",
                      "Asset",
                    )}`,
                    id: "new",
                  },
                ]
              : [
                  ...data.data.data,
                  {
                    name: `Add ${getModuleNamingBySlug(
                      organizationData?.organizationData,
                      "assets",
                      "Asset",
                    )}`,
                    id: "new",
                  },
                ]
            : [
                ...data.data.data,
                {
                  name: `Add ${getModuleNamingBySlug(
                    organizationData?.organizationData,
                    "assets",
                    "Asset",
                  )}`,
                  id: "new",
                },
              ],
        );

      setListingHelper &&
        setListingHelper((prev) =>
          prev.map((item, index) => {
            if (index === targetIndex) {
              const currentList = item?.asset?.listing;
              let assetListing =
                data.data.data?.length > 0
                  ? data.data.data?.map((item) => ({
                      ...item,
                      value: item?.id,
                      label: item?.name
                        ? `${item.name}${item?.code ? ` (${item?.code})` : ""}`
                        : "",
                    }))
                  : [];

              return {
                ...item,
                asset: {
                  ...item.asset,
                  listing: Array.isArray(currentList)
                    ? appendData && data?.data?.data
                      ? [
                          ...currentList,
                          ...assetListing,
                          {
                            label: `Add ${getModuleNamingBySlug(
                              organizationData?.organizationData,
                              "assets",
                              "Asset",
                            )}`,
                            value: "new",
                          },
                        ]
                      : [
                          ...data.data.data,
                          {
                            label: `Add ${getModuleNamingBySlug(
                              organizationData?.organizationData,
                              "assets",
                              "Asset",
                            )}`,
                            value: "new",
                          },
                        ]
                    : [
                        ...data.data.data,
                        {
                          label: `Add ${getModuleNamingBySlug(
                            organizationData?.organizationData,
                            "assets",
                            "Asset",
                          )}`,
                          value: "new",
                        },
                      ],
                },
              };
            }
            return item;
          }),
        );

      return data.data.last_page >= page ? true : false;
    }
    setAssetList &&
      setAssetList([
        {
          name: `Add ${getModuleNamingBySlug(
            organizationData?.organizationData,
            "assets",
            "Asset",
          )}`,
          id: "new",
        },
      ]);
    setListingHelper &&
      setListingHelper((prev) =>
        prev.map((item, index) => {
          if (index === targetIndex) {
            return {
              ...item,
              asset: {
                ...item.asset,
                listing: [
                  {
                    label: `Add ${getModuleNamingBySlug(
                      organizationData?.organizationData,
                      "assets",
                      "Asset",
                    )}`,
                    value: "new",
                  },
                ],
              },
            };
          }

          return item;
        }),
      );
    return null;
  } catch (e) {
    setAssetList &&
      setAssetList([
        {
          name: `Add ${getModuleNamingBySlug(
            organizationData?.organizationData,
            "assets",
            "Asset",
          )}`,
          id: "new",
        },
      ]);
    setListingHelper &&
      setListingHelper((prev) =>
        prev.map((item, index) => {
          if (index === targetIndex) {
            return {
              ...item,
              asset: {
                ...item.asset,
                listing: [
                  {
                    label: `Add ${getModuleNamingBySlug(
                      organizationData?.organizationData,
                      "assets",
                      "Asset",
                    )}`,
                    value: "new",
                  },
                ],
              },
            };
          }

          return item;
        }),
      );
    setAssetLoading(false);
    return false;
  }
};

export const useBankList = () => {
  const [bankList, setBankList] = useState([]);
  const [bankLoading, setBankLoading] = useState(false);
  const init = useCallback(async () => {
    try {
      setBankLoading(true);
      const { data } = await get(`${API.GET_BANK_LIST}?all=1`);
      setBankLoading(false);
      const accountList =
        data?.data?.data && data?.data?.data?.length > 0
          ? data?.data?.data?.map((item) => {
              const list = {
                label: item?.abbreviation + "-" + item?.name,
                value: item?.id,
                rest: item,
              };
              return list;
            })
          : [];
      setBankList(accountList);
    } catch (e) {
      setBankList([]);
      setBankLoading(false);
    }
  }, []);

  useEffect(() => {
    init();
  }, [init]);

  return {
    bankList,
    bankLoading,
  };
};

export const ContactList = async (
  organizationData,
  trustId,
  page,
  setContactLoading,
  setContactList,
  appendData,
  amlctfContact = false,
  search = "",
) => {
  try {
    if (trustId) {
      setContactLoading(true);

      const { data } = await get(
        `${API.GET_CONTACTS}?trust_id=${trustId}&limit=50&page=${page}&search=${search}&is_aml_ctf=${amlctfContact ? 1 : ""}`,
      );

      setContactLoading(false);

      setContactList((prevList) =>
        Array.isArray(prevList)
          ? appendData && data?.data?.data
            ? [
                ...prevList,
                ...data.data.data,
                {
                  first_name: "",
                  last_name: `Add ${getModuleNamingBySlug(
                    organizationData?.organizationData,
                    "contacts",
                    "Customer",
                  )}`,
                  id: "new",
                },
              ]
            : [
                ...data.data.data,
                {
                  first_name: "",
                  last_name: `Add ${getModuleNamingBySlug(
                    organizationData?.organizationData,
                    "contacts",
                    "Customer",
                  )}`,
                  id: "new",
                },
              ]
          : [
              ...data.data.data,
              {
                first_name: "",
                last_name: `Add ${getModuleNamingBySlug(
                  organizationData?.organizationData,
                  "contacts",
                  "Customer",
                )}`,
                id: "new",
              },
            ],
      );

      return data.data.last_page >= page ? true : false;
    } else {
      setContactList([
        {
          first_name: "",
          last_name: `Add ${getModuleNamingBySlug(
            organizationData?.organizationData,
            "contacts",
            "Customer",
          )}`,
          id: "new",
        },
      ]);
      return false;
    } // Return whether there's more data
  } catch (e) {
    setContactList([
      {
        first_name: "",
        last_name: `Add ${getModuleNamingBySlug(
          organizationData?.organizationData,
          "contacts",
          "Customer",
        )}`,
        id: "new",
      },
    ]);
    setContactLoading(false);
    return false;
  }
};

export const ContractList = async (
  trustId,
  contactId = "",
  subAssetId,
  page,
  setContractLoading,
  setContractList,
  appendData,
  setListingHelper,
  targetIndex,
  search = "",
) => {
  try {
    if (subAssetId || contactId) {
      setContractLoading(true);

      const { data } = await get(
        `${API.GET_CONTRACTS}?contact_id=${[contactId]}&sub_asset_id=${[
          subAssetId,
        ]}&trust_id=${trustId}&limit=50&page=${page}&search=${search}`,
      );

      setContractLoading(false);

      setContractList &&
        setContractList((prevList) =>
          Array.isArray(prevList)
            ? appendData && data?.data?.data
              ? [
                  ...prevList,
                  ...data.data.data,
                  { contract_number: "Add Contract", id: "new" },
                ]
              : [
                  ...data.data.data,
                  { contract_number: "Add Contract", id: "new" },
                ]
            : [
                ...data.data.data,
                { contract_number: "Add Contract", id: "new" },
              ],
        );

      setListingHelper &&
        setListingHelper((prev) =>
          prev.map((item, index) => {
            if (index === targetIndex) {
              const currentList = item?.contract?.listing;
              let contractListing =
                data.data.data?.length > 0
                  ? data.data.data?.map((item) => ({
                      ...item,
                      value: item?.id,
                      label: item?.contract_number ? item.contract_number : "",
                    }))
                  : [];

              return {
                ...item,
                contract: {
                  ...item.contract,
                  listing: Array.isArray(currentList)
                    ? appendData && data?.data?.data
                      ? [
                          ...currentList,
                          ...contractListing,
                          { label: "Add Contract", value: "new" },
                        ]
                      : [
                          ...data.data.data,
                          { label: "Add Contract", value: "new" },
                        ]
                    : [
                        ...data.data.data,
                        { label: "Add Contract", value: "new" },
                      ],
                },
              };
            }
            return item;
          }),
        );

      return data.data.last_page >= page ? true : false;
    }
    setContractList &&
      setContractList([{ contract_number: "Add Contract", id: "new" }]);
    setListingHelper &&
      setListingHelper((prev) =>
        prev.map((item, index) => {
          if (index === targetIndex) {
            return {
              ...item,
              contract: {
                ...item.contract,
                listing: [{ label: "Add Contract", value: "new" }],
              },
            };
          }

          return item;
        }),
      );
    return null;
  } catch (e) {
    setContractList &&
      setContractList([{ contract_number: "Add Contract", id: "new" }]);
    setListingHelper &&
      setListingHelper((prev) =>
        prev.map((item, index) => {
          if (index === targetIndex) {
            return {
              ...item,
              contract: {
                ...item.contract,
                listing: [{ label: "Add Contract", value: "new" }],
              },
            };
          }

          return item;
        }),
      );
    setContractLoading(false);
    return false;
  }
};

export const ReceiptList = async (
  trustId,
  contactId = "",
  subAssetId,
  page,
  setReceiptLoading,
  setReceiptList,
  appendData,
  setListingHelper,
  targetIndex,
  payment_id,
  search = "",
) => {
  try {
    if (subAssetId) {
      setReceiptLoading(true);

      const { data } = await get(
        payment_id
          ? `${API.GET_RECEIPT}?sub_asset_id=${[
              subAssetId,
            ]}&trust_id=${trustId}&limit=-1&page=${page}&search=${search}&is_payment=1&payment_id=${payment_id}&sort_column=receipt_number&sort_order=asc`
          : `${API.GET_RECEIPT}?sub_asset_id=${[
              subAssetId,
            ]}&trust_id=${trustId}&limit=-1&page=${page}&search=${search}&is_payment=1&sort_column=receipt_number&sort_order=asc`,
      );

      setReceiptLoading(false);

      setReceiptList &&
        setReceiptList((prevList) =>
          Array.isArray(prevList)
            ? appendData && data?.data?.data
              ? [...prevList, ...data.data.data]
              : [...data.data.data]
            : [...data.data.data],
        );

      setListingHelper &&
        setListingHelper((prev) =>
          prev.map((item, index) => {
            if (index === targetIndex) {
              const currentList = item?.receipt?.listing;
              let receiptListing =
                data.data.data?.length > 0
                  ? data.data.data?.map((item) => ({
                      ...item,
                      value: item?.id,
                      label: `${item?.receipt_number} ${
                        item?.invoice_number ? ` - ${item.invoice_number}` : ""
                      } ${
                        item.remaining_balance
                          ? ` ($${formatNumber(
                              Number(item.remaining_balance),
                            )})`
                          : item?.amount
                            ? ` ($${formatNumber(Number(item.amount))})`
                            : ""
                      }`,
                    }))
                  : [];

              return {
                ...item,
                receipt: {
                  ...item.receipt,
                  listing: Array.isArray(currentList)
                    ? appendData && data?.data?.data
                      ? [...currentList, ...receiptListing]
                      : [...data.data.data]
                    : [...data.data.data],
                },
              };
            }
            return item;
          }),
        );

      return data.data.last_page >= page ? true : false;
    }
    setReceiptList && setReceiptList([]);
    setListingHelper &&
      setListingHelper((prev) =>
        prev.map((item, index) => {
          if (index === targetIndex) {
            return {
              ...item,
              receipt: {
                ...item.receipt,
                listing: [],
              },
            };
          }

          return item;
        }),
      );
    return null;
  } catch (e) {
    setReceiptList && setReceiptList([]);
    setListingHelper &&
      setListingHelper((prev) =>
        prev.map((item, index) => {
          if (index === targetIndex) {
            return {
              ...item,
              receipt: {
                ...item.receipt,
                listing: [],
              },
            };
          }

          return item;
        }),
      );
    setReceiptLoading(false);
    return false;
  }
};

export const orgContactList = async (
  trustId,
  page,
  setContactLoading,
  setContactList,
  appendData,
  search = "",
) => {
  if (trustId?.length > 0) {
    try {
      setContactLoading(true);

      const { data } = await get(
        `${API.GET_CONTACTS}?trust_id=${
          Array.isArray(trustId) && trustId?.length > 0
            ? trustId?.join(",")
            : trustId
              ? trustId
              : ""
        }&limit=50&page=${page}&search=${search}`,
      );

      setContactLoading(false);

      setContactList((prevList) =>
        Array.isArray(prevList)
          ? appendData && data?.data?.data
            ? [...prevList, ...data.data.data]
            : [...data.data.data]
          : [...data.data.data],
      );

      return data.data.last_page >= page ? true : false;
    } catch (e) {
      setContactList([]);
      setContactLoading(false);
      return false;
    }
  }
};

export const JobList = async (
  trustId,
  page,
  setJobLoading,
  setJobList,
  appendData,
  setListingHelper,
  targetIndex,
  search = "",
) => {
  try {
    if (trustId) {
      setJobLoading(true);

      const { data } = await get(
        `${API.GET_JOBS}?trust_id=${trustId}&limit=50&page=${page}&search=${search}`,
      );

      setJobLoading(false);

      setJobList &&
        setJobList((prevList) =>
          Array.isArray(prevList)
            ? appendData && data?.data?.data
              ? [...prevList, ...data.data.data, { code: "Add Job", id: "new" }]
              : [...data.data.data, { code: "Add Job", id: "new" }]
            : [...data.data.data, { code: "Add Job", id: "new" }],
        );

      setListingHelper &&
        setListingHelper((prev) =>
          prev.map((item, index) => {
            if (index === targetIndex) {
              const currentList = item?.job?.listing;

              let jobListing =
                data.data.data?.length > 0
                  ? data.data.data?.map((item) => ({
                      ...item,
                      value: item?.id,
                      label: item?.code ? item.code : "",
                    }))
                  : [];

              return {
                ...item,
                job: {
                  ...item.job,
                  listing: Array.isArray(currentList)
                    ? appendData && data?.data?.data
                      ? [
                          ...currentList,
                          ...jobListing,
                          { code: "Add Job", value: "new" },
                        ]
                      : [...data.data.data, { label: "Add Job", value: "new" }]
                    : [...data.data.data, { label: "Add Job", value: "new" }],
                },
              };
            }
            return item;
          }),
        );

      return data.data.last_page >= page ? true : false;
    } else {
      setJobList && setJobList([{ title: "Add Job", id: "new" }]);
      setListingHelper &&
        setListingHelper((prev) =>
          prev.map((item, index) => {
            if (index === targetIndex) {
              return {
                ...item,
                job: {
                  ...item.job,
                  listing: [{ label: "Add Job", value: "new" }],
                },
              };
            }

            return item;
          }),
        );
      return false;
    } // Return whether there's more data
  } catch (e) {
    setJobList && setJobList([{ title: "Add Job", id: "new" }]);
    setJobLoading(false);
    setListingHelper &&
      setListingHelper((prev) =>
        prev.map((item, index) => {
          if (index === targetIndex) {
            return {
              ...item,
              job: {
                ...item.job,
                listing: [{ label: "Add Job", value: "new" }],
              },
            };
          }

          return item;
        }),
      );
    return false;
  }
};

export const TaskList = async (
  jobId,
  trustId,
  page,
  setTaskLoading,
  setTaskList,
  appendData,
  setListingHelper,
  targetIndex,
  search = "",
) => {
  try {
    if (trustId && jobId) {
      setTaskLoading(true);

      const { data } = await get(
        `${API.GET_TASKS}?trust_id=${trustId}&limit=50&page=${page}&search=${search}&ts_job_id=${jobId}`,
      );

      setTaskLoading(false);
      setTaskList &&
        setTaskList((prevList) =>
          Array.isArray(prevList)
            ? appendData && data?.data?.data
              ? [
                  ...prevList,
                  ...data.data.data,
                  { code: "Add Task", id: "new" },
                ]
              : [...data.data.data, { code: "Add Task", id: "new" }]
            : [...data.data.data, { code: "Add Task", id: "new" }],
        );

      setListingHelper &&
        setListingHelper((prev) =>
          prev.map((item, index) => {
            if (index === targetIndex) {
              const currentList = item?.task?.listing;
              let taskListing =
                data.data.data?.length > 0
                  ? data.data.data?.map((item) => ({
                      ...item,
                      value: item?.id,
                      label: item?.code ? item.code : "",
                    }))
                  : [];

              return {
                ...item,
                task: {
                  ...item.task,
                  listing: Array.isArray(currentList)
                    ? appendData && data?.data?.data
                      ? [
                          ...currentList,
                          ...taskListing,
                          { label: "Add Task", value: "new" },
                        ]
                      : [...data.data.data, { label: "Add Task", value: "new" }]
                    : [...data.data.data, { label: "Add Task", value: "new" }],
                },
              };
            }
            return item;
          }),
        );

      return data.data.last_page >= page ? true : false;
    } else {
      setTaskList && setTaskList([{ title: "Add Task", id: "new" }]);
      setListingHelper &&
        setListingHelper((prev) =>
          prev.map((item, index) => {
            if (index === targetIndex) {
              return {
                ...item,
                task: {
                  ...item.task,
                  listing: [{ label: "Add Task", value: "new" }],
                },
              };
            }

            return item;
          }),
        );
      return false;
    } // Return whether there's more data
  } catch (e) {
    setTaskList && setTaskList([{ title: "Add Task", id: "new" }]);
    setTaskLoading(false);
    setListingHelper &&
      setListingHelper((prev) =>
        prev.map((item, index) => {
          if (index === targetIndex) {
            return {
              ...item,
              task: {
                ...item.task,
                listing: [{ label: "Add Task", value: "new" }],
              },
            };
          }

          return item;
        }),
      );
    return false;
  }
};

export const SubAssetList = async (
  organizationData,
  trustId,
  assetId,
  contactId = "",
  page,
  setSubAssetLoading,
  setSubAssetList,
  appendData,
  setListingHelper,
  targetIndex,
  vendorId = "",
  payment_id,
  payee = "",
  search = "",
) => {
  try {
    if (assetId || contactId || true) {
      setSubAssetLoading(true);

      const { data } = await get(
        payment_id
          ? `${API.GET_SUB_ASSETS}?trust_id=${trustId}&asset_id=${assetId}&vendor_id=${vendorId}&contact_id=${contactId}&limit=50&page=${page}&search=${search}&payment_id=${payment_id}${payee ? `&payee=${payee}` : ""}`
          : `${API.GET_SUB_ASSETS}?trust_id=${trustId}&asset_id=${assetId}&vendor_id=${vendorId}&contact_id=${contactId}&limit=50&page=${page}&search=${search}${payee ? `&payee=${payee}` : ""}`,
      );

      setSubAssetLoading(false);

      setSubAssetList &&
        setSubAssetList((prevList) =>
          Array.isArray(prevList)
            ? appendData && data?.data?.data
              ? [
                  ...prevList,
                  ...data.data.data,
                  {
                    name: `Add ${getModuleNamingBySlug(
                      organizationData?.organizationData,
                      "sub-assets",
                      "Sub Asset",
                    )}`,
                    id: "new",
                  },
                ]
              : [
                  ...data.data.data,
                  {
                    name: `Add ${getModuleNamingBySlug(
                      organizationData?.organizationData,
                      "sub-assets",
                      "Sub Asset",
                    )}`,
                    id: "new",
                  },
                ]
            : [
                ...data.data.data,
                {
                  name: `Add ${getModuleNamingBySlug(
                    organizationData?.organizationData,
                    "sub-assets",
                    "Sub Asset",
                  )}`,
                  id: "new",
                },
              ],
        );

      setListingHelper &&
        setListingHelper((prev) =>
          prev.map((item, index) => {
            if (index === targetIndex) {
              const currentList = item.sub_asset.listing;
              let subAssetListing =
                data.data.data?.length > 0
                  ? data.data.data?.map((item) => ({
                      ...item,
                      value: item?.id,
                      label: item?.name
                        ? `${item.name}${
                            item?.identifier ? ` (${item?.identifier})` : ""
                          }`
                        : "",
                    }))
                  : [];

              return {
                ...item,
                sub_asset: {
                  ...item.sub_asset,
                  listing: Array.isArray(currentList)
                    ? appendData
                      ? [
                          ...currentList,
                          ...subAssetListing,
                          {
                            label: `Add ${getModuleNamingBySlug(
                              organizationData?.organizationData,
                              "sub-assets",
                              "Sub Asset",
                            )}`,
                            value: "new",
                          },
                        ]
                      : [
                          ...data.data.data,
                          {
                            label: `Add ${getModuleNamingBySlug(
                              organizationData?.organizationData,
                              "sub-assets",
                              "Sub Asset",
                            )}`,
                            value: "new",
                          },
                        ]
                    : [
                        ...data.data.data,
                        {
                          label: `Add ${getModuleNamingBySlug(
                            organizationData?.organizationData,
                            "sub-assets",
                            "Sub Asset",
                          )}`,
                          value: "new",
                        },
                      ],
                },
              };
            }
            return item;
          }),
        );

      return data.data.last_page >= page ? true : false;
    }
    setSubAssetList &&
      setSubAssetList([
        {
          name: `Add ${getModuleNamingBySlug(
            organizationData?.organizationData,
            "sub-assets",
            "Sub Asset",
          )}`,
          id: "new",
        },
      ]);
    setListingHelper &&
      setListingHelper((prev) =>
        prev.map((item, index) => {
          if (index === targetIndex) {
            return {
              ...item,
              sub_asset: {
                ...item.sub_asset,
                listing: [
                  {
                    label: `Add ${getModuleNamingBySlug(
                      organizationData?.organizationData,
                      "sub-assets",
                      "Sub Asset",
                    )}`,
                    value: "new",
                  },
                ],
              },
            };
          }

          return item;
        }),
      );
    return null;
  } catch (e) {
    setSubAssetList &&
      setSubAssetList([
        {
          name: `Add ${getModuleNamingBySlug(
            organizationData?.organizationData,
            "sub-assets",
            "Sub Asset",
          )}`,
          id: "new",
        },
      ]);
    setListingHelper &&
      setListingHelper((prev) =>
        prev.map((item, index) => {
          if (index === targetIndex) {
            return {
              ...item,
              sub_asset: {
                ...item.sub_asset,
                listing: [
                  {
                    label: `Add ${getModuleNamingBySlug(
                      organizationData?.organizationData,
                      "sub-assets",
                      "Sub Asset",
                    )}`,
                    value: "new",
                  },
                ],
              },
            };
          }

          return item;
        }),
      );
    setSubAssetLoading(false);
    return false;
  }
};

export const orgSubAssetList = async (
  trustId,
  vendorId,
  page,
  setSubAssetLoading,
  setSubAssetList,
  appendData,
  search = "",
) => {
  if (trustId?.length > 0) {
    try {
      setSubAssetLoading(true);

      const { data } = await get(
        `${API.GET_SUB_ASSETS}?trust_id=${
          Array.isArray(trustId) && trustId?.length > 0
            ? trustId?.join(",")
            : trustId
              ? trustId
              : ""
        }&vendor_id=${
          Array.isArray(vendorId) && vendorId?.length > 0
            ? vendorId?.join(",")
            : vendorId
              ? vendorId
              : ""
        }&limit=50&page=${page}&search=${search}`,
      );

      setSubAssetLoading(false);

      setSubAssetList((prevList) =>
        Array.isArray(prevList)
          ? appendData && data?.data?.data
            ? [...prevList, ...data.data.data]
            : [...data.data.data]
          : [...data.data.data],
      );

      return data.data.last_page >= page ? true : false;
    } catch (e) {
      setSubAssetList([]);
      setSubAssetLoading(false);
      return false;
    }
  }
};

export const SupplierList = async (
  trustId,
  page,
  setSupplierLoading,
  setSupplierList,
  appendData,
  search = "",
) => {
  try {
    setSupplierLoading(true);

    const { data } = await get(
      `${API.GET_SUPPLIERS}?trust_id=${trustId}&limit=50&page=${page}&search=${search}`,
    );

    setSupplierLoading(false);

    setSupplierList((prevList) =>
      Array.isArray(prevList)
        ? appendData && data?.data?.data
          ? [...prevList, ...data.data.data]
          : data.data.data
        : data.data.data,
    );

    return data.data.last_page >= page ? true : false;
  } catch (e) {
    setSupplierList([]);
    setSupplierLoading(false);
    return false;
  }
};

export const BeneficaryList = async (
  trustId,
  vendorId,
  page,
  setBeneficaryLoading,
  setBeneficaryList,
  appendData,
  setListingHelper,
  targetIndex,
  search = "",
) => {
  try {
    if (vendorId) {
      setBeneficaryLoading(true);

      const { data } = await get(
        `${API.GET_BENEFICARYS}?trust_id=${trustId}&vendor_id=${vendorId}&limit=50&page=${page}&search=${search}`,
      );

      setBeneficaryLoading(false);

      setBeneficaryList &&
        setBeneficaryList((prevList) =>
          Array.isArray(prevList)
            ? appendData && data?.data?.data
              ? [...prevList, ...data.data.data]
              : data.data.data
            : data.data.data,
        );

      setListingHelper &&
        setListingHelper((prev) =>
          prev.map((item, index) => {
            if (index === targetIndex) {
              const currentList = item?.beneficiary?.listing;
              let beneficaryListing =
                data.data.data?.length > 0
                  ? data.data.data?.map((item) => ({
                      ...item,
                      value: item?.id,
                      label: `${formatSubcontractorName(item)}${
                        item?.code ? ` (${item?.code})` : ""
                      }`,
                    }))
                  : [];

              return {
                ...item,
                beneficiary: {
                  ...item.beneficiary,
                  listing: Array.isArray(currentList)
                    ? appendData
                      ? [...currentList, ...beneficaryListing]
                      : data.data.data
                    : data.data.data,
                },
              };
            }
            return item;
          }),
        );

      return data.data.last_page >= page ? true : false;
    }
    return null;
  } catch (e) {
    setBeneficaryList([]);
    setBeneficaryLoading(false);
    return false;
  }
};

export const TrustList = async (
  orgId,
  page,
  setTrustLoading,
  setTrustList,
  appendData,
  search = "",
) => {
  try {
    if (orgId) {
      setTrustLoading(true);

      const { data } = await get(
        `${API.GET_TRUSTS}?organization_id=${orgId}&limit=50&page=${page}&search=${search}`,
      );

      setTrustLoading(false);

      setTrustList((prevList) =>
        Array.isArray(prevList)
          ? appendData && data?.data?.data
            ? [
                ...prevList,
                ...data.data.data,
                { first_name: "", last_name: "Add Trust", id: "new" },
              ]
            : [
                ...data.data.data,
                { first_name: "", last_name: "Add Trust", id: "new" },
              ]
          : [
              ...data.data.data,
              { first_name: "", last_name: "Add Trust", id: "new" },
            ],
      );

      return data.data.last_page >= page ? true : false;
    } else {
      return false;
    } // Return whether there's more data
  } catch (e) {
    setTrustList([{ first_name: "", last_name: "Add Trust", id: "new" }]);
    setTrustLoading(false);
    return false;
  }
};

export const OrganizationTypeList = async (
  setOrganizationTypeLoading,
  setOrganizationTypeList,
  appendData,
  search = "",
) => {
  try {
    setOrganizationTypeLoading(true);

    const { data } = await get(
      `${API.GET_ORGANIZATION_TYPES}?search=${search}`,
    );

    setOrganizationTypeLoading(false);

    setOrganizationTypeList((prevList) =>
      Array.isArray(prevList)
        ? appendData && data?.data
          ? [...prevList, ...data.data]
          : [...data.data]
        : [...data.data],
    );

    return false;
  } catch (e) {
    setOrganizationTypeList([]);
    setOrganizationTypeLoading(false);
    return false;
  }
};

export const OfficeList = async (
  org_id,
  page,
  setOfficeLoading,
  setOfficeList,
  appendData,
  search = "",
) => {
  try {
    setOfficeLoading(true);

    const { data } = await get(
      `${API.OFFICE}?organization_id=${
        // eslint-disable-next-line valid-typeof
        Array.isArray(org_id) ? org_id.join(",") : org_id
      }&limit=100&page=${page}&search=${search}`,
    );

    setOfficeLoading(false);

    setOfficeList((prevList) =>
      Array.isArray(prevList)
        ? appendData && data?.data?.data
          ? [...prevList, ...data?.data?.data]
          : data?.data?.data
        : data?.data?.data,
    );

    return data.data.last_page >= page ? true : false;
  } catch (e) {
    setOfficeList([]);
    setOfficeLoading(false);
    return false;
  }
};

export const useRoleList = (admin = false) => {
  const [roleList, setRoleList] = useState([]);
  const [roleLoading, setRoleLoading] = useState(false);
  const init = useCallback(async () => {
    try {
      setRoleLoading(true);
      const { data } = await get(
        `${API.GET_ROLE_LIST}${admin ? "?type=Admin" : "?type=Organization"}`,
      );
      setRoleLoading(false);
      const list =
        data?.data?.data && data?.data?.data?.length > 0
          ? data?.data?.data?.map((item) => {
              const list = {
                label: item?.name,
                value: item?.id,
                rest: item,
              };
              return list;
            })
          : [];
      setRoleList(list);
    } catch (e) {
      setRoleList([]);
      setRoleLoading(false);
    }
  }, [admin]);

  useEffect(() => {
    init();
  }, [init]);

  return {
    roleList,
    roleLoading,
  };
};

export const useAdminRoleList = () => {
  const [roleList, setRoleList] = useState([]);
  const [roleLoading, setRoleLoading] = useState(false);
  const init = useCallback(async () => {
    try {
      setRoleLoading(true);
      const { data } = await get(`${API.GET_ADMIN_ROLE_LIST}`);
      setRoleLoading(false);
      const list =
        data?.data?.data && data?.data?.data?.length > 0
          ? data?.data?.data?.map((item) => {
              const list = {
                label: item?.name,
                value: item?.id,
                rest: item,
              };
              return list;
            })
          : [];
      setRoleList(list);
    } catch (e) {
      setRoleList([]);
      setRoleLoading(false);
    }
  }, []);

  useEffect(() => {
    init();
  }, [init]);

  return {
    roleList,
    roleLoading,
  };
};

export const UserList = async (
  orgId,
  compliance,
  page,
  setUserLoading,
  setUserList,
  appendData,
  search = "",
) => {
  try {
    if (orgId) {
      setUserLoading(true);

      const { data } = await get(
        `${
          API.USER
        }?organization_id=${orgId}&limit=50&page=${page}&search=${search}${
          compliance ? `&is_compliance=1` : ""
        }`,
      );

      setUserLoading(false);

      setUserList((prevList) =>
        Array.isArray(prevList)
          ? appendData && data?.data?.data
            ? [...prevList, ...data.data.data]
            : data.data.data
          : data.data.data,
      );

      return data.data.last_page >= page ? true : false;
    }
  } catch (e) {
    setUserList([]);
    setUserLoading(false);
    return false;
  }
};

export const useUserList = (orgId) => {
  const [userList, setUserList] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const init = useCallback(async () => {
    if (orgId) {
      try {
        setUserLoading(true);
        const { data } = await get(
          `${API.USER}?organization_id=${orgId}&limit=-1&page=1`,
        );
        setUserLoading(false);
        const list =
          data?.data?.data && data?.data?.data?.length > 0
            ? data?.data?.data?.map((item) => {
                const list = {
                  label: formatSubcontractorName(item),
                  value: item?.id,
                  ...item,
                };
                return list;
              })
            : [];
        setUserList(list);
      } catch (e) {
        setUserList([]);
        setUserLoading(false);
      }
    }
  }, [orgId]);

  useEffect(() => {
    init();
  }, [init]);

  return {
    userList,
    userLoading,
  };
};

export const DiscountCodeList = async (
  trustId,
  page,
  setDiscountCodeLoading,
  setDiscountCodeList,
  appendData,
  setListingHelper,
  targetIndex,
  search = "",
) => {
  try {
    if (trustId) {
      setDiscountCodeLoading(true);

      const { data } = await get(
        `${API.GET_DISCOUNT_CODES}?trust_id=${trustId}&limit=50&page=${page}&search=${search}`,
      );

      setDiscountCodeLoading(false);

      setDiscountCodeList &&
        setDiscountCodeList((prevList) =>
          Array.isArray(prevList)
            ? appendData && data?.data?.data
              ? [
                  ...prevList,
                  ...data.data.data,
                  { title: "Add Discount Code", id: "new" },
                ]
              : [...data.data.data, { title: "Add Discount Code", id: "new" }]
            : [...data.data.data, { title: "Add Discount Code", id: "new" }],
        );

      setListingHelper &&
        setListingHelper((prev) =>
          prev.map((item, index) => {
            if (index === targetIndex) {
              const currentList = item?.discount?.listing;
              let discountListing =
                data.data.data?.length > 0
                  ? data.data.data?.map((item) => ({
                      ...item,
                      value: item?.id,
                      label: item?.code
                        ? `${item.code} (${
                            item?.discount_type
                              ? `${item.discount}%`
                              : `$${item?.discount}`
                          })`
                        : "",
                    }))
                  : [];

              return {
                ...item,
                discount: {
                  ...item.discount,
                  listing: Array.isArray(currentList)
                    ? appendData && data?.data?.data
                      ? [
                          ...currentList,
                          ...discountListing,
                          { label: "Add Discount Code", value: "new" },
                        ]
                      : [
                          ...data.data.data,
                          { label: "Add Discount Code", value: "new" },
                        ]
                    : [
                        ...data.data.data,
                        { label: "Add Discount Code", value: "new" },
                      ],
                },
              };
            }
            return item;
          }),
        );

      return data.data.last_page >= page ? true : false;
    } else {
      setDiscountCodeList &&
        setDiscountCodeList([{ title: "Add Discount Code", id: "new" }]);
      setListingHelper &&
        setListingHelper((prev) =>
          prev.map((item, index) => {
            if (index === targetIndex) {
              return {
                ...item,
                discount: {
                  ...item.discount,
                  listing: [{ label: "Add Discount Code", value: "new" }],
                },
              };
            }

            return item;
          }),
        );
      return false;
    } // Return whether there's more data
  } catch (e) {
    setDiscountCodeList &&
      setDiscountCodeList([{ title: "Add Discount Code", id: "new" }]);
    setDiscountCodeLoading(false);
    setListingHelper &&
      setListingHelper((prev) =>
        prev.map((item, index) => {
          if (index === targetIndex) {
            return {
              ...item,
              discount: {
                ...item.discount,
                listing: [{ label: "Add Discount Code", value: "new" }],
              },
            };
          }

          return item;
        }),
      );
    return false;
  }
};

export const usePremiumList = (trustId) => {
  const [premiumList, setPremiumList] = useState([]);
  const [showPremium, setShowPremium] = useState(false);
  const [premiumLoading, setPremiumLoading] = useState(false);
  const init = useCallback(async () => {
    try {
      setPremiumLoading(true);
      const { data } = await get(`${API.GET_TRUSTS}/${trustId}`);
      setPremiumLoading(false);
      setShowPremium(data?.data?.is_premium == 1 ? true : true);
      setPremiumList(data?.data?.premiums);
    } catch (e) {
      setPremiumList([]);
      setPremiumLoading(false);
    }
  }, [trustId]);

  useEffect(() => {
    init();
  }, [init]);

  return {
    premiumList,
    premiumLoading,
    showPremium,
  };
};

export const useDiscountCodeList = (trustId) => {
  const [discountCodeList, setDiscountCodeList] = useState([]);
  const [discountCodeLoading, setDiscountCodeLoading] = useState(false);
  const init = useCallback(async () => {
    try {
      setDiscountCodeLoading(true);
      const { data } = await get(
        `${API.GET_DISCOUNT_CODES}?trust_id=${trustId}&limit=-1&page=1`,
      );
      setDiscountCodeLoading(false);
      setDiscountCodeList(data?.data?.data);
    } catch (e) {
      setDiscountCodeList([]);
      setDiscountCodeLoading(false);
    }
  }, [trustId]);

  useEffect(() => {
    init();
  }, [init]);

  return {
    discountCodeList,
    discountCodeLoading,
  };
};

export const useProcessingFeeList = (trustId) => {
  const [processingFeeList, setProcessingFeeList] = useState([]);
  const [processingFeeLoading, setProcessingFeeLoading] = useState(false);
  const init = useCallback(async () => {
    try {
      setProcessingFeeLoading(true);
      const { data } = await get(
        `${API.GET_PROCESSING_FEE_CODES}?trust_id=${trustId}&limit=-1&page=1`,
      );
      setProcessingFeeLoading(false);
      setProcessingFeeList(data?.data?.data);
    } catch (e) {
      setProcessingFeeList([]);
      setProcessingFeeLoading(false);
    }
  }, [trustId]);

  useEffect(() => {
    init();
  }, [init]);

  return {
    processingFeeList,
    processingFeeLoading,
  };
};

export const AdminSectionList = async (
  organizationType,
  sectionType,
  page,
  setSectionLoading,
  setSectionList,
  appendData,
  search = "",
) => {
  if (sectionType && organizationType) {
    try {
      setSectionLoading(true);

      const { data } = await get(
        `${API.GET_ADMIN_SECTIONS}?organization_type_id=${organizationType}&section_type=${sectionType}&limit=-1&page=${page}&search=${search}`,
      );

      setSectionLoading(false);

      setSectionList((prevList) =>
        Array.isArray(prevList)
          ? appendData && data?.data?.data
            ? [...prevList, ...data.data.data]
            : [...data.data.data]
          : [...data.data.data],
      );

      return data.data.last_page >= page ? true : false;
    } catch (e) {
      setSectionList([]);
      setSectionLoading(false);
      return false;
    }
  }
};

export const AllContactList = async (
  trustId,
  page,
  setAllContactLoading,
  setAllContactList,
  appendData,
  search = "",
) => {
  try {
    if (trustId) {
      setAllContactLoading(true);

      const { data } = await get(
        `${API.GET_ALL_CONTACTS}?trust_id=${trustId}&limit=50&page=${page}&search=${search}`,
      );

      setAllContactLoading(false);

      setAllContactList((prevList) =>
        Array.isArray(prevList)
          ? appendData && data?.data?.data
            ? [...prevList, ...data.data.data]
            : [...data.data.data]
          : [...data.data.data],
      );

      return data.data.last_page >= page ? true : false;
    } else {
      setAllContactList([]);
      return false;
    } // Return whether there's more data
  } catch (e) {
    setAllContactList([]);
    setAllContactLoading(false);
    return false;
  }
};

export const ReceiptItemList = async (
  trustId,
  contactId = "",
  subAssetId,
  page,
  setReceiptItemLoading,
  setReceiptItemList,
  appendData,
  setListingHelper,
  targetIndex,
  mainOpeningBalanceData = "",
  payment_id,
  search = "",
) => {
  try {
    if (subAssetId) {
      setReceiptItemLoading(true);

      const { data } = await get(
        payment_id
          ? `${API.GET_RECEIPT_ITEMS}?sub_asset_id=${[
              subAssetId,
            ]}&trust_id=${trustId}&limit=-1&page=${page}&search=${search}&is_payment=1&payment_id=${payment_id}&sort_column=receipt_number&sort_order=asc`
          : `${API.GET_RECEIPT_ITEMS}?sub_asset_id=${[
              subAssetId,
            ]}&trust_id=${trustId}&limit=-1&page=${page}&search=${search}&is_payment=1&sort_column=receipt_number&sort_order=asc`,
      );

      setReceiptItemLoading(false);

      setReceiptItemList &&
        setReceiptItemList((prevList) =>
          Array.isArray(prevList)
            ? appendData && data?.data?.data
              ? [...prevList, ...data.data.data]
              : [...data.data.data]
            : [...data.data.data],
        );

      setListingHelper &&
        setListingHelper((prev) =>
          prev.map((item, index) => {
            if (index === targetIndex) {
              const currentList = item?.receipt?.listing;
              let receiptListing =
                data.data.data?.length > 0
                  ? data.data.data?.map((item) => ({
                      ...item,
                      value: item?.id,
                      label: `${item?.receipt?.receipt_number} ${
                        item?.receipt?.invoice_number
                          ? ` - ${item.receipt?.invoice_number}`
                          : ""
                      } ${
                        item.remaining_balance
                          ? ` ($${formatNumber(
                              Number(item.remaining_balance),
                            )})`
                          : item?.amount
                            ? ` ($${formatNumber(Number(item.amount))})`
                            : ""
                      }`,
                    }))
                  : [];

              return {
                ...item,
                receipt: {
                  ...item.receipt,
                  listing: Array.isArray(currentList)
                    ? appendData && data?.data?.data
                      ? mainOpeningBalanceData
                        ? [
                            mainOpeningBalanceData,
                            ...currentList,
                            ...receiptListing,
                          ]
                        : [...currentList, ...receiptListing]
                      : mainOpeningBalanceData
                        ? [mainOpeningBalanceData, ...data.data.data]
                        : [...data.data.data]
                    : mainOpeningBalanceData
                      ? [mainOpeningBalanceData, ...data.data.data]
                      : [...data.data.data],
                },
              };
            }
            return item;
          }),
        );

      return data.data.last_page >= page ? true : false;
    }
    setReceiptItemList && setReceiptItemList([]);
    setListingHelper &&
      setListingHelper((prev) =>
        prev.map((item, index) => {
          if (index === targetIndex) {
            return {
              ...item,
              receipt: {
                ...item.receipt,
                listing: [],
              },
            };
          }

          return item;
        }),
      );
    return null;
  } catch (e) {
    setReceiptItemList && setReceiptItemList([]);
    setListingHelper &&
      setListingHelper((prev) =>
        prev.map((item, index) => {
          if (index === targetIndex) {
            return {
              ...item,
              receipt: {
                ...item.receipt,
                listing: [],
              },
            };
          }

          return item;
        }),
      );
    setReceiptItemLoading(false);
    return false;
  }
};

export const AllSuburbList = async (
  state_id,
  page,
  setAllSuburbLoading,
  setAllSuburbList,
  appendData,
  search = "",
) => {
  try {
    setAllSuburbLoading(true);

    const { data } = await get(
      `${API.GET_ALL_SUBURB_LIST}?state=${state_id}&limit=-1&page=${page}&search=${search}`,
    );
    setAllSuburbLoading(false);

    setAllSuburbList((prevList) =>
      Array.isArray(prevList)
        ? appendData && data?.data?.data
          ? [...prevList, ...data.data.data]
          : data.data.data
        : data.data.data,
    );

    return data.data.last_page >= page ? true : false;
  } catch (e) {
    setAllSuburbList([]);
    setAllSuburbLoading(false);
    return false;
  }
};

export const useCountryList = () => {
  const [countryList, setCountryList] = useState([]);
  const [countryLoading, setCountryLoading] = useState(false);
  const init = useCallback(async () => {
    try {
      setCountryLoading(true);
      const { data } = await get(API.GET_COUNTRY_LIST);
      setCountryLoading(false);
      setCountryList(data?.data);
    } catch (e) {
      setCountryList([]);
      setCountryLoading(false);
    }
  }, []);

  useEffect(() => {
    init();
  }, [init]);

  return {
    countryList,
    countryLoading,
  };
};

export const VersionList = async (
  setVersionLoading,
  setVersionList,
  appendData,
  search = "",
) => {
  try {
    setVersionLoading(true);

    const { data } = await get(`${API.GET_ADMIN_VERSIONS}?search=${search}`);

    setVersionLoading(false);

    setVersionList((prevList) =>
      Array.isArray(prevList)
        ? appendData && data?.data?.data
          ? [...prevList, ...data.data.data]
          : [...data.data.data]
        : [...data.data.data],
    );

    return false;
  } catch (e) {
    setVersionList([]);
    setVersionLoading(false);
    return false;
  }
};
